import "@hotwired/turbo-rails"
import { Application } from "@hotwired/stimulus"
import * as ActiveStorage from "@rails/activestorage"
import { onFrameMissing, onFrameFetchRequestError } from "./frame_fallback"

// Import controllers
import ChartController from "./controllers/chart_controller"
import ClientSwitcherController from "./controllers/client_switcher_controller"
import DropdownController from "./controllers/dropdown_controller"
import DropzoneController from "./controllers/dropzone_controller"
import DynamicFormController from "./controllers/dynamic_form_controller"
import FileUploadController from "./controllers/file_upload_controller"
import FlashMessagesController from "./controllers/flash_messages_controller" 
import FormController from "./controllers/form_controller"
import ImportFormController from "./controllers/import_form_controller"
import IntegrationsSetupController from "./controllers/integrations_setup_controller"
import NotificationFilterController from "./controllers/notification_filter_controller"
import HoverCardController from "./controllers/hover_card_controller"
import IcpSwitcherController from "./controllers/icp_switcher_controller"
import LeadFiltersController from "./controllers/lead_filters_controller"
import MappingFeedbackController from "./controllers/mapping_feedback_controller"
import ModalController from "./controllers/modal_controller"
import NotificationsCenterController from "./controllers/notifications_center_controller"
import PlatformFormController from "./controllers/platform_form_controller"
import SidebarController from "./controllers/sidebar_controller"
import SuppressionController from "./controllers/suppression_controller"
import WatchlistController from "./controllers/watchlist_controller"
import SearchController from "./controllers/search_controller"
import SelectAllController from "./controllers/select_all_controller"
import TurboStreamsController from "./controllers/turbo_streams_controller"
import TabsController from "./controllers/tabs_controller"
import TeamInvitationsController from "./controllers/team_invitations_controller"
import TooltipController from "./controllers/tooltip_controller"
import OnboardingWizardController from "./controllers/onboarding_wizard_controller"
import OnboardingCompanyAnalysisController from "./controllers/onboarding/company_analysis_controller"
import OnboardingIcpController from "./controllers/onboarding/icp_controller"
import OnboardingExampleModeController from "./controllers/onboarding/example_mode_controller"
import OnboardingFileUploadController from "./controllers/onboarding/file_upload_controller"
import OnboardingIntakeFillController from "./controllers/onboarding/intake_fill_controller"
import LeadsInboxController from "./controllers/leads_inbox_controller"
import InboxLanesController from "./controllers/inbox_lanes_controller"
import ConversationComposerController from "./controllers/conversation_composer_controller"
import EmailFinderController from "./controllers/email_finder_controller"
import LeadNotesController from "./controllers/lead_notes_controller"
import MessageEditorController from "./controllers/message_editor_controller"
import ResponsePanelController from "./controllers/response_panel_controller"
import CollapsibleController from "./controllers/collapsible_controller"
import ExpandableController from "./controllers/expandable_controller"
import BulkSelectController from "./controllers/bulk_select_controller"
import ContextMenuController from "./controllers/context_menu_controller"
import CampaignMapperController from "./controllers/campaign_mapper_controller"
import CampaignMessagingController from "./controllers/campaign_messaging_controller"
import CampaignPickerController from "./controllers/campaign_picker_controller"
import AutoSubmitController from "./controllers/auto_submit_controller"
import DecideCardController from "./controllers/decide_card_controller"
import DismissableController from "./controllers/dismissable_controller"
import EventSlackUrlController from "./controllers/event_slack_url_controller"
import HelloController from "./controllers/hello_controller"
import JobTitleChipsController from "./controllers/job_title_chips_controller"
import ModalOpenerController from "./controllers/modal_opener_controller"
import PollReloadController from "./controllers/poll_reload_controller"
import PriorityInboxController from "./controllers/priority_inbox_controller"
import SlackConfigController from "./controllers/slack_config_controller"
import TimelineItemController from "./controllers/timeline_item_controller"
import ToastController from "./controllers/toast_controller"
import ToggleController from "./controllers/toggle_controller"
import WebhookConfigController from "./controllers/webhook_config_controller"
import WebhookEventSettingsController from "./controllers/webhook_event_settings_controller"
import WebhookEventToggleController from "./controllers/webhook_event_toggle_controller"



// What happens when a frame's request does not come back with the frame: the
// app-wide rule (a redirect lands as a full-page visit, known-issues #58) and
// the exception for frames that carry their own fallback. Both live in
// frame_fallback.js so spec/frontend/dom can run the real handlers.
document.addEventListener("turbo:frame-missing", onFrameMissing)
document.addEventListener("turbo:fetch-request-error", onFrameFetchRequestError)

// Start ActiveStorage
ActiveStorage.start()

// Initialize Stimulus
window.Stimulus = Application.start()

// Register controllers
Stimulus.register("chart", ChartController)
Stimulus.register("client-switcher", ClientSwitcherController)
Stimulus.register("dropdown", DropdownController)
Stimulus.register("dropzone", DropzoneController)
Stimulus.register("dynamic-form", DynamicFormController)
Stimulus.register("file-upload", FileUploadController)
Stimulus.register("flash-messages", FlashMessagesController)
Stimulus.register("form", FormController)
Stimulus.register("mapping-feedback", MappingFeedbackController)
Stimulus.register("hover-card", HoverCardController)
Stimulus.register("icp-switcher", IcpSwitcherController)
Stimulus.register("import-form", ImportFormController)
Stimulus.register("integrations-setup", IntegrationsSetupController)
Stimulus.register("lead-filters", LeadFiltersController)
Stimulus.register("modal", ModalController)
Stimulus.register("notifications-center", NotificationsCenterController)
Stimulus.register("notification-filter", NotificationFilterController)
Stimulus.register("platform-form", PlatformFormController)
Stimulus.register("search", SearchController)
Stimulus.register("select-all", SelectAllController)
Stimulus.register("sidebar", SidebarController)
Stimulus.register("suppression", SuppressionController)
Stimulus.register("watchlist", WatchlistController)
Stimulus.register("turbo-streams", TurboStreamsController)
Stimulus.register("tabs", TabsController)
Stimulus.register("team-invitations", TeamInvitationsController)
Stimulus.register("tooltip", TooltipController)
Stimulus.register("onboarding-wizard", OnboardingWizardController)
Stimulus.register("onboarding--company-analysis", OnboardingCompanyAnalysisController)
Stimulus.register("onboarding--icp", OnboardingIcpController)
Stimulus.register("onboarding--example-mode", OnboardingExampleModeController)
Stimulus.register("onboarding--file-upload", OnboardingFileUploadController)
Stimulus.register("onboarding--intake-fill", OnboardingIntakeFillController)
Stimulus.register("leads-inbox", LeadsInboxController)
Stimulus.register("inbox-lanes", InboxLanesController)
Stimulus.register("conversation-composer", ConversationComposerController)
Stimulus.register("email-finder", EmailFinderController)
Stimulus.register("lead-notes", LeadNotesController)
Stimulus.register("message-editor", MessageEditorController)
Stimulus.register("response-panel", ResponsePanelController)
Stimulus.register("collapsible", CollapsibleController)
Stimulus.register("expandable", ExpandableController)
Stimulus.register("bulk-select", BulkSelectController)
Stimulus.register("context-menu", ContextMenuController)
Stimulus.register("campaign-mapper", CampaignMapperController)
Stimulus.register("campaign-messaging", CampaignMessagingController)
Stimulus.register("campaign-picker", CampaignPickerController)
Stimulus.register("auto-submit", AutoSubmitController)
Stimulus.register("decide-card", DecideCardController)
Stimulus.register("dismissable", DismissableController)
Stimulus.register("event-slack-url", EventSlackUrlController)
Stimulus.register("hello", HelloController)
Stimulus.register("job-title-chips", JobTitleChipsController)
Stimulus.register("modal-opener", ModalOpenerController)
Stimulus.register("poll-reload", PollReloadController)
Stimulus.register("priority-inbox", PriorityInboxController)
Stimulus.register("slack-config", SlackConfigController)
Stimulus.register("timeline-item", TimelineItemController)
Stimulus.register("toast", ToastController)
Stimulus.register("toggle", ToggleController)
Stimulus.register("webhook-config", WebhookConfigController)
Stimulus.register("webhook-event-settings", WebhookEventSettingsController)
Stimulus.register("webhook-event-toggle", WebhookEventToggleController)


// Ensure Chartkick is initialized on turbo:load
document.addEventListener("turbo:load", () => {
  console.log("Turbo:load fired");
  
  // Small delay to ensure all scripts are loaded
  setTimeout(() => {
    // Initialize charts if Chartkick is available
    if (typeof Chartkick !== 'undefined') {
      Chartkick.refresh();
    }
  }, 100);
});

// Simplified Turbo History Manager for Frame-based Navigation
// Works with Turbo's built-in navigation while ensuring proper URL updates

class TurboHistoryManager {
  constructor() {
    this.mainContentFrameSelector = '[id*="main_content"]'
    this.isNavigating = false
    this.debug = false // Disable debug by default
    this.setupEventListeners()
    
    if (this.debug) {
      console.log("Turbo History Manager initialized")
    }
  }

  setupEventListeners() {
    // Handle frame renders and update URL accordingly
    document.addEventListener("turbo:frame-render", (event) => {
      this.handleFrameRender(event)
    })

    // Handle browser back/forward buttons
    window.addEventListener("popstate", (event) => {
      this.handlePopstate(event)
    })

    // Handle frame load events
    document.addEventListener("turbo:frame-load", (event) => {
      this.handleFrameLoad(event)
    })
  }

  handleFrameRender(event) {
    const frame = event.target
    
    // Only handle main content frame navigation
    if (!this.isMainContentFrame(frame)) {
      return
    }

    // Update URL if frame has a source and it's different from current URL
    if (frame.src && !this.isNavigating) {
      this.updateUrlFromFrame(frame)
    }
  }

  handlePopstate(event) {
    // Prevent infinite loops
    if (this.isNavigating) {
      return
    }

    const mainFrame = document.querySelector(this.mainContentFrameSelector)
    if (!mainFrame) {
      // Fallback to full page navigation if no main frame
      window.location.reload()
      return
    }

    // Get the URL from the browser location
    const targetUrl = window.location.href
    
    // Navigate the frame to match the browser URL
    this.isNavigating = true
    
    try {
      mainFrame.src = targetUrl
      
      // Dispatch custom event for sidebar and other components
      window.dispatchEvent(new CustomEvent('turbo:history-restore', {
        detail: { url: targetUrl, frame: mainFrame }
      }))
      
      if (this.debug) {
        console.log('History restore:', targetUrl)
      }
    } finally {
      // Reset navigation flag after a short delay
      setTimeout(() => {
        this.isNavigating = false
      }, 100)
    }
  }

  handleFrameLoad(event) {
    const frame = event.target
    
    if (this.isMainContentFrame(frame)) {
      // Dispatch event for other components that might need to react
      window.dispatchEvent(new CustomEvent('turbo:main-content-loaded', {
        detail: { frame: frame, url: frame.src }
      }))
    }
  }

  updateUrlFromFrame(frame) {
    if (!frame.src) {
      return
    }

    try {
      const frameUrl = new URL(frame.src)
      const currentUrl = new URL(window.location.href)
      
      // Only update if the path or search params are different
      if (frameUrl.pathname !== currentUrl.pathname || 
          frameUrl.search !== currentUrl.search) {
        
        const newUrl = frameUrl.pathname + frameUrl.search + frameUrl.hash
        
        // Determine the action based on frame's data-turbo-action attribute
        const action = frame.dataset.turboAction || 'advance'
        
        // Update browser history using the appropriate method
        if (action === 'replace') {
          window.history.replaceState(null, '', newUrl)
        } else {
          window.history.pushState(null, '', newUrl)
        }
        
        // Dispatch custom event for sidebar and other components
        window.dispatchEvent(new CustomEvent('turbo:url-updated', {
          detail: { 
            url: newUrl, 
            frame: frame, 
            action: action
          }
        }))
        
        if (this.debug) {
          console.log('URL updated:', newUrl, 'action:', action)
        }
      }
    } catch (error) {
      console.warn('Error updating URL from frame:', error)
    }
  }

  isMainContentFrame(frame) {
    return frame && frame.id && frame.id.includes('main_content')
  }

  // Public API for programmatic navigation
  navigateTo(url, options = {}) {
    const mainFrame = document.querySelector(this.mainContentFrameSelector)
    if (!mainFrame) {
      // Fallback to full page navigation using Turbo.visit
      if (window.Turbo && window.Turbo.visit) {
        Turbo.visit(url, { action: options.action || "advance" })
      } else {
        window.location.href = url
      }
      return
    }

    // Set the action attribute and navigate
    const action = options.action || 'advance'
    mainFrame.setAttribute('data-turbo-action', action)
    mainFrame.src = url
    
    // Clean up the attribute after navigation
    setTimeout(() => {
      mainFrame.removeAttribute('data-turbo-action')
    }, 100)
  }

  refresh() {
    const mainFrame = document.querySelector(this.mainContentFrameSelector)
    if (!mainFrame) {
      window.location.reload()
      return
    }

    // Use replace action for refresh
    mainFrame.setAttribute('data-turbo-action', 'replace')
    
    // Add cache-busting parameter
    const currentSrc = mainFrame.src || window.location.href
    const url = new URL(currentSrc)
    url.searchParams.set('_refresh', Date.now())
    
    mainFrame.src = url.toString()
    
    setTimeout(() => {
      mainFrame.removeAttribute('data-turbo-action')
    }, 100)
  }

  replacePage(url) {
    const mainFrame = document.querySelector(this.mainContentFrameSelector)
    if (!mainFrame) {
      if (window.Turbo && window.Turbo.visit) {
        Turbo.visit(url, { action: "replace" })
      } else {
        window.location.replace(url)
      }
      return
    }

    // Set replace action
    mainFrame.setAttribute('data-turbo-action', 'replace')
    mainFrame.src = url
    
    setTimeout(() => {
      mainFrame.removeAttribute('data-turbo-action')
    }, 100)
  }
}

// Initialize the history manager when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  // Initialize the history manager
  const turboHistoryManager = new TurboHistoryManager()

  // Export for global access
  window.TurboHistoryManager = turboHistoryManager

  // Create global navigation helpers for backward compatibility
  window.LeanGTMNavigation = {
    navigateTo: (url, options = {}) => turboHistoryManager.navigateTo(url, options),
    refresh: () => turboHistoryManager.refresh(),
    replacePage: (url) => turboHistoryManager.replacePage(url),
    forceFullNavigation: (url) => {
      if (window.Turbo && window.Turbo.visit) {
        Turbo.visit(url, { action: "advance" })
      } else {
        window.location.href = url
      }
    }
  }

  console.log('Turbo History Manager initialized')
})